import { useState, useCallback } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { SignOnScreen, BuddyList, IMWindow, NewIMDialog } from '@/components/aim';

const Index = () => {
  useSeoMeta({
    title: 'Buddy Chat - Nostr Instant Messenger',
    description: 'A retro AOL Instant Messenger style chat app powered by the Nostr protocol.',
  });

  const { user } = useCurrentUser();
  const [openChats, setOpenChats] = useState<string[]>([]);
  const [showNewIMDialog, setShowNewIMDialog] = useState(false);

  const handleSelectBuddy = useCallback((pubkey: string) => {
    if (!openChats.includes(pubkey)) {
      setOpenChats((prev) => [...prev, pubkey]);
    }
  }, [openChats]);

  const handleCloseChat = useCallback((pubkey: string) => {
    setOpenChats((prev) => prev.filter((p) => p !== pubkey));
  }, []);

  const handleNewIM = useCallback(() => {
    setShowNewIMDialog(true);
  }, []);

  const handleStartNewChat = useCallback((pubkey: string) => {
    setShowNewIMDialog(false);
    handleSelectBuddy(pubkey);
  }, [handleSelectBuddy]);

  // Show sign on screen if not logged in
  if (!user) {
    return <SignOnScreen />;
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] p-4">
      {/* Desktop Layout */}
      <div className="flex gap-4 items-start">
        {/* Buddy List */}
        <div className="flex-shrink-0">
          <BuddyList
            onSelectBuddy={handleSelectBuddy}
            selectedPubkey={openChats[openChats.length - 1] || null}
            onNewIM={handleNewIM}
          />
        </div>

        {/* IM Windows */}
        <div className="flex-1 flex flex-wrap gap-4 items-start">
          {openChats.map((pubkey) => (
            <IMWindow
              key={pubkey}
              pubkey={pubkey}
              onClose={() => handleCloseChat(pubkey)}
            />
          ))}

          {/* Empty State */}
          {openChats.length === 0 && (
            <div className="flex-1 flex items-center justify-center min-h-[300px]">
              <div className="text-center text-[hsl(var(--muted-foreground))]">
                <p className="text-sm">Double-click a buddy to start chatting</p>
                <p className="text-xs mt-1">or click the IM button to message someone new</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New IM Dialog */}
      {showNewIMDialog && (
        <NewIMDialog
          onClose={() => setShowNewIMDialog(false)}
          onStartChat={handleStartNewChat}
        />
      )}
    </div>
  );
};

export default Index;
